import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ai-thinking-overlay",
  id: "ai-thinking-overlay"
}
const _hoisted_2 = { class: "ai-thinking-content" }
const _hoisted_3 = { class: "ai-thinking-text" }
const _hoisted_4 = { class: "step-indicator" }
const _hoisted_5 = {
  key: 0,
  class: "step-check"
}
const _hoisted_6 = {
  key: 1,
  class: "step-loading"
}
const _hoisted_7 = {
  key: 2,
  class: "step-dot"
}
const _hoisted_8 = { class: "step-text" }
const _hoisted_9 = { class: "ai-progress" }
const _hoisted_10 = { class: "progress-bar" }
const _hoisted_11 = { class: "progress-text" }

import { ref, onMounted, nextTick, watch } from 'vue';
import { aiAnimationService } from '@/utils/aiAnimationService';

interface ThinkingStep {
  id: number;
  text: string;
}

interface AIThinkingOverlayProps {
  steps?: ThinkingStep[];
  stepDelay?: number;
  onComplete?: () => void;
  loadStatus?: boolean;
  apiCompleted?: boolean;
  detailDataLoaded?: boolean; // 详情页数据是否已加载完成
  startStepIndex?: number; // 从第几步开始（0 = 第 1 步，8 = 第 9 步）
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AIThinkingOverlay',
  props: {
    steps: { default: () => [
    { id: 1, text: '正在读取实控人征信数据...' },
    { id: 2, text: '正在同步企业工商基础信息...' },
    { id: 3, text: '正在拉取亚马逊店铺经营数据...' },
    { id: 4, text: '正在分析近 12 个月销售及回款数据...' },
    { id: 5, text: '正在计算店铺资产与库存价值...' },
    { id: 6, text: '正在拆解商品品类与定价结构...' },
    { id: 7, text: '正在排查企业合规风险点...' },
    { id: 8, text: '正在分析企业刚性负债情况...' },
    { id: 9, text: '正在生成风控终审评级数据...' },
    { id: 10, text: '正在汇总尽调数据模块...' },
    { id: 11, text: '正在生成报告可视化图表...' },
    { id: 12, text: '正在排版最终尽调报告内容...' }
  ] },
    stepDelay: { default: 800 },
    onComplete: {},
    loadStatus: { type: Boolean },
    apiCompleted: { type: Boolean },
    detailDataLoaded: { type: Boolean },
    startStepIndex: { default: 0 }
  },
  emits: ["complete", "step9Complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const visible = ref(false);
const currentStepIndex = ref(-1);
const progressPercent = ref(0);
const stepsContainerRef = ref<HTMLElement | null>(null);
const stepRefs = ref<any[]>([]);
const hasCompleted = ref(false); // 标记动画是否已完成
const apiCompletedLocal = ref(false); // 标记 API（customerLeaderAdopt）是否已完成
const detailDataLoadedLocal = ref(false); // 标记详情页数据是否已加载完成

const runAIThinking = async () => {
  hasCompleted.value = false;
  apiCompletedLocal.value = false;
  detailDataLoadedLocal.value = false;
  const totalSteps = props.steps.length;
  const startTime = Date.now();
  const minDuration = totalSteps * props.stepDelay; // 最小执行时间（12 步 × 800ms = 9.6 秒）

  console.log('开始动画', {
    totalSteps,
    stepDelay: props.stepDelay,
    minDuration,
    startStepIndex: props.startStepIndex,
    initialApiCompleted: props.apiCompleted
  });

  // 如果 startStepIndex > 0，直接显示前几步为已完成状态
  if (props.startStepIndex > 0) {
    currentStepIndex.value = props.startStepIndex - 1;
    progressPercent.value = Math.round((props.startStepIndex / totalSteps) * 100);
    console.log(`从第 ${props.startStepIndex + 1} 步开始，前 ${props.startStepIndex} 步已标记为完成`);
  }

  for (let i = props.startStepIndex; i < totalSteps; i++) {
    currentStepIndex.value = i;

    // 第 9 步（索引 8）的特殊处理：需要等待 API 完成才能继续
    if (i === 8 && !props.apiCompleted) {
      console.log('执行到第 9 步，API 未完成，等待 API 完成...');

      // 使用 watch 监听 apiCompleted 变化，避免轮询
      await new Promise(resolve => {
        const unwatch = watch(() => props.apiCompleted, (newValue) => {
          if (newValue) {
            console.log('API 已完成，继续执行');
            unwatch(); // 停止监听
            resolve(true);
          }
        }, { immediate: false });
      });
    }

    // 根据状态控制进度条显示
    const shouldWaitForData = !props.detailDataLoaded && i >= 11;
    const isLastStep = i === totalSteps - 1;
    console.log(`步骤 ${i + 1}, detailDataLoaded: ${props.detailDataLoaded}, shouldWaitForData: ${shouldWaitForData}`);

    if ((!props.apiCompleted && i >= 8) || shouldWaitForData || isLastStep) {
      // API 未完成时（第 9 步及以后）或详情页数据未完成时（最后一步）或正在执行最后一步时，进度控制在 99% 以内
      progressPercent.value = Math.min(99, Math.round(((i + 1) / totalSteps) * 99));
      console.log(`进度控制在 99%: ${progressPercent.value}%`);
    } else {
      // 正常显示进度
      progressPercent.value = Math.round(((i + 1) / totalSteps) * 100);
      console.log(`正常进度: ${progressPercent.value}%`);
    }

    // 滚动到当前步骤
    await nextTick();
    scrollToCurrentStep(i);

    // 如果是最后一步且详情页数据未加载完成，强制进度为 99%
    if (i === totalSteps - 1 && !props.detailDataLoaded) {
      progressPercent.value = 99;
      console.log('最后一步，详情页数据未加载，进度设置为 99%');
    }

    await new Promise(resolve => setTimeout(resolve, props.stepDelay));

    // 第 9 步（索引 8）执行完成后，触发跳转但不关闭
    if (i === 8) {
      console.log('第 9 步执行完成，触发跳转');
      emit('step9Complete');
    }
  }

  // 所有步骤执行完成后，检查是否已经过了最小时间
  const elapsed = Date.now() - startTime;
  const remainingTime = Math.max(0, minDuration - elapsed);

  console.log('动画步骤执行完成', {
    elapsed,
    remainingTime,
    minDuration,
    apiCompleted: props.apiCompleted,
    detailDataLoaded: props.detailDataLoaded
  });

  // 如果详情页数据已加载完成，不需要等待最小时间，直接关闭
  if (props.detailDataLoaded) {
    console.log('详情页数据已加载，跳过等待最小时间');
  } else if (remainingTime > 0) {
    // 否则等待剩余时间
    console.log(`等待剩余时间 ${remainingTime}ms`);
    await new Promise(resolve => setTimeout(resolve, remainingTime));
  }

  // 等待详情页数据加载完成
  if (!props.detailDataLoaded) {
    console.log('等待详情页数据加载完成...');
    // 确保进度条保持在 99%
    progressPercent.value = 99;
    await new Promise(resolve => {
      const checkData = setInterval(() => {
        if (props.detailDataLoaded) {
          clearInterval(checkData);
          resolve(true);
        }
      }, 100);
    });
    console.log('详情页数据已加载完成');
    // 数据加载完成后，进度跳到 100%
    progressPercent.value = 100;
  } else {
    console.log('详情页数据已经完成，无需等待');
  }

  // 延迟关闭
  await new Promise(resolve => setTimeout(resolve, 500));
  visible.value = false;
  emit('complete');
};

// 滚动到当前步骤
const scrollToCurrentStep = (index: number) => {
  const container = stepsContainerRef.value;
  const stepElement = stepRefs.value[index];

  if (container && stepElement) {
    const containerRect = container.getBoundingClientRect();
    const stepRect = stepElement.getBoundingClientRect();

    // 计算需要滚动的距离
    const scrollTop = container.scrollTop + stepRect.top - containerRect.top - 80;

    // 平滑滚动
    container.scrollTo({
      top: scrollTop,
      behavior: 'smooth'
    });
  }
};

onMounted(() => {
  // 不自动执行
});

// 监听 loadStatus 变化
watch(() => props.loadStatus, (newVal) => {
  // console.log('loadStatus changed:', newVal)

  if (newVal === true && !visible.value && !hasCompleted.value) {
    // 开始显示动画
    visible.value = true
    runAIThinking()
  }
}, { immediate: true });

__expose({})

return (_ctx: any,_cache: any) => {
  return (visible.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[1] || (_cache[1] = _createStaticVNode("<div class=\"ai-brain\" data-v-45fb2506><div class=\"brain-ring\" data-v-45fb2506></div><div class=\"brain-ring\" data-v-45fb2506></div><div class=\"brain-ring\" data-v-45fb2506></div><div class=\"brain-core\" data-v-45fb2506><div class=\"brain-icon\" data-v-45fb2506><svg viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\" data-v-45fb2506><path d=\"M12 2C9.5 2 7.5 3.5 7.5 6C7.5 7.5 8.2 8.8 9.3 9.6C9.1 10.1 9 10.6 9 11.2C9 12.8 10.3 14.1 12 14.1C13.7 14.1 15 12.8 15 11.2C15 10.6 14.9 10.1 14.7 9.6C15.8 8.8 16.5 7.5 16.5 6C16.5 3.5 14.5 2 12 2Z\" fill=\"currentColor\" opacity=\"0.9\" data-v-45fb2506></path><path d=\"M12 15C9.5 15 7.5 16.5 7.5 19C7.5 20.5 8.7 21.7 10.2 21.9L10.4 21.9L10.6 21.9C11.1 21.9 11.5 21.5 11.5 21C11.5 20.5 11.1 20.1 10.6 20.1C10.5 20.1 10.4 20.1 10.3 20.1C9.7 19.9 9.3 19.4 9.3 18.8C9.3 17.5 10.4 16.5 11.8 16.5H12.2C13.6 16.5 14.7 17.5 14.7 18.8C14.7 19.4 14.3 19.9 13.7 20.1C13.6 20.1 13.5 20.1 13.4 20.1C12.9 20.1 12.5 20.5 12.5 21C12.5 21.5 12.9 21.9 13.4 21.9L13.6 21.9L13.8 21.9C15.3 21.7 16.5 20.5 16.5 19C16.5 16.5 14.5 15 12 15Z\" fill=\"currentColor\" data-v-45fb2506></path><circle cx=\"10.5\" cy=\"7.5\" r=\"1.5\" fill=\"#fff\" opacity=\"0.6\" data-v-45fb2506></circle><circle cx=\"13.5\" cy=\"7.5\" r=\"1.5\" fill=\"#fff\" opacity=\"0.6\" data-v-45fb2506></circle></svg></div></div></div>", 1)),
          _createElementVNode("div", _hoisted_3, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "thinking-title" }, [
              _createElementVNode("span", { class: "title-icon" }, "✨"),
              _createTextVNode(" AI 智能分析中 "),
              _createElementVNode("span", { class: "title-icon" }, "✨")
            ], -1)),
            _createElementVNode("div", {
              class: "thinking-steps",
              ref_key: "stepsContainerRef",
              ref: stepsContainerRef
            }, [
              _createVNode(_TransitionGroup, { name: "step-fade" }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.steps, (step, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: step.id,
                      class: _normalizeClass(["thinking-step", {
                   active: currentStepIndex.value >= index,
                   completed: currentStepIndex.value > index
                 }]),
                      ref_for: true,
                      ref: el => stepRefs.value[index] = el
                    }, [
                      _createElementVNode("div", _hoisted_4, [
                        (currentStepIndex.value > index)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_5, "✓"))
                          : (currentStepIndex.value === index)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                            : (_openBlock(), _createElementBlock("span", _hoisted_7, "•"))
                      ]),
                      _createElementVNode("span", _hoisted_8, _toDisplayString(step.text), 1)
                    ], 2))
                  }), 128))
                ]),
                _: 1
              })
            ], 512),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("div", {
                  class: "progress-fill",
                  style: _normalizeStyle({ width: progressPercent.value + '%' })
                }, null, 4)
              ]),
              _createElementVNode("div", _hoisted_11, _toDisplayString(progressPercent.value) + "%", 1)
            ])
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})